import React from "react";
import {
	FiBookmark,
	FiCheckCircle,
	FiLink
} from "react-icons/fi";
import type { APAADMINBaseModelsContentListItem } from "../../API/Models";
import { countLinks, countReferences } from "./utils";

export interface ContentGridViewProps {
	items: APAADMINBaseModelsContentListItem[];
	resolveContentTypeLabel: (contentType?: string | null) => string;
	onNavigate: (item: APAADMINBaseModelsContentListItem) => void;
}

const ContentGridView: React.FC<ContentGridViewProps> = ({
	items,
	resolveContentTypeLabel,
	onNavigate
}) => {
	const totalRowCount = items.length;

	return (
		<div className="grid grid-cols-2 md:grid-cols-3 gap-4">
			{totalRowCount === 0 ? (
				<div className="col-span-full rounded-2xl border border-slate-200 bg-white p-4 shadow-sm text-sm text-slate-600">
					No content found.
				</div>
			) : (
				items.map((p) => {
					const isPage = (p.contentType ?? "").toLowerCase() === "page";
					const decodedSlug =
						isPage && p.slug != null
							? (() => {
									try {
										return decodeURIComponent(p.slug);
									} catch {
										return p.slug;
									}
							  })()
							: "";
					const meta = (p.metadata ?? {}) as { description?: string };
					const linksCount = isPage ? countLinks(p) : null;
					const refsCount = isPage ? countReferences(p) : null;
					const publicPath = p.id ? `/page/${p.id}` : `/${decodedSlug || ""}`;
					const typeLabel = resolveContentTypeLabel(p.contentType);
					const title = p.title?.trim() || (isPage ? decodedSlug || "Untitled" : typeLabel || "Untitled");
					const subtitle = meta.description ?? (isPage ? decodedSlug : p.language ?? "");
					const detail = isPage ? decodedSlug || publicPath : `lang: ${p.language || "\u2014"}`;
					return (
						<div
							key={`${p.contentType}-${p.slug ?? ""}-${p.id ?? ""}`}
							className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm cursor-pointer hover:border-slate-300 transition"
							onClick={() => onNavigate(p)}
						>
							<div className="flex flex-col gap-2 min-w-0">
								<span className="inline-flex w-fit items-center rounded-full border border-slate-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
									{typeLabel || "content"}
								</span>
								<h3 className="text-base font-semibold text-slate-900 truncate" title={title}>
									{title}
								</h3>
								<p className="text-sm text-slate-600 line-clamp-2 min-h-[2.5rem]">
									{subtitle || "\u2014"}
								</p>
								{p.id && (
									<p className="text-[11px] font-mono text-slate-500 truncate" title={p.id}>
										id: {p.id}
									</p>
								)}
								<p className="text-xs font-mono text-slate-500 truncate" title={detail}>
									{detail}
								</p>
							</div>
							<div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-500">
								{isPage ? (
									<>
										<span className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2 py-0.5">
											<FiLink className="text-slate-400 shrink-0" /> {linksCount}
										</span>
										<span className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2 py-0.5">
											<FiBookmark className="text-slate-400 shrink-0" /> {refsCount}
										</span>
										<span className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2 py-0.5">
											<FiCheckCircle className="text-emerald-500 shrink-0" /> Published
										</span>
									</>
								) : (
									<span className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2 py-0.5">
										<FiCheckCircle className="text-emerald-500 shrink-0" /> Content
									</span>
								)}
							</div>
						</div>
					);
				})
			)}
		</div>
	);
};

export default ContentGridView;
